import { checkHasPermission } from "shared/imports/permissionFunctions"
import { oneLine } from "shared/imports/stringFunctions"

const PRESS_CONTACT_LIMIT = 5000
const PRESS_CONTACT_UNSUBSCRIBE_THRESHOLD = 50
const RECIPIENT_UNSUBSCRIBE_THRESHOLD = 5000
const AB_TEST_MINIMUM = 100

export const outboxConstants = {
    // arbitrary suggested smallest number of recipients for an AB test email campaign
    AB_TEST_MINIMUM,

    AB_TEST_MINIMUM_WARNING: oneLine`
        You have entered fewer than ${AB_TEST_MINIMUM} participants to your
        test which may cause inconclusive results.
    `,

    // constant borrowed from backend, userdata/outbox/types.py
    ATTACHMENT_MAX_SIZE: 17800000,

    EMAIL_LIMIT_PERCENTAGE_THRESHOLD: 75,
    REPLY_TO_SLUG: "reply_to",
    FROM_SLUG: "from",

    RECIPIENT_TOOL_DEBOUNCE_DELAY_TIME: 2000,
    // TODO: consolidate in sharedConstants once this ff is live
    DEFAULT_QUORUM_DOMAIN: ".quorumoutbox.com",

    // OutboxBanner Constants
    INTERNAL_IP_WARNING: oneLine`
        Emailing internal Contacts? Make sure your IT team has whitelisted Quorum's new IP address for maximum deliverability.
        If you're not sure, reach out to your CSM or support@quorum.us
    `,

    // Intrado's contractual limit per Email
    PRESS_CONTACT_LIMIT,
    PRESS_CONTACT_LIMIT_WARNING: oneLine`
        Due to data provider limits you may only send an outbox email to 5,000 press contacts.
        Please select fewer press recipients to proceed with sending.
        Reach out via Live Chat or to support@quorum.us if you have questions.
    `,
    // BulkEmails with >= this number of Press recipients must include an unsubscribe link
    PRESS_CONTACT_UNSUBSCRIBE_THRESHOLD,
    // BulkEmails with >= this number of Recipients must include an unsubscribe link
    RECIPIENT_UNSUBSCRIBE_THRESHOLD,
    PRECONFIGURED_UNSUBSCRIBE_TOOLTIP: oneLine`
        Enabling this feature provides your recipients with standard messaging that will allow them to opt out of your emails.
        It is also automatically set to 'Show Unsubscribe' when you add the unsubscribe placeholder${
            checkHasPermission([DjangIO.app.models.PermissionType.media_relations])
                ? `, or when you add more than ${PRESS_CONTACT_UNSUBSCRIBE_THRESHOLD} press contacts.`
                : "."
        }
        Please keep in mind any legal requirements related to including unsubscribe links.
        The content or recipients of your email may require you to follow GDPR or CAN-SPAM regulations.
    `,
    CUSTOM_UNSUBSCRIBE_TOOLTIP: oneLine`
        Enabling this feature provides your recipients with a customized message that will allow them to opt out of your emails.
        Please keep in mind any legal requirements related to including unsubscribe links.
        The content or recipients of your email may require you to follow GDPR or CAN-SPAM regulations.
    `,
    UNSUBSCRIBE_OFF_TOOLTIP: oneLine`
        Enabling this feature will not allow your recipients to opt out of your emails.
    `,
    UNSUBSCRIBE_DISABLED_DESCRIPTION: oneLine`
        Because you are using a sending domain provided by Quorum and shared between multiple clients, we require unsubscribe links on all messages.
        Please contact your Customer Success Manager or support@quorum.us if you’d like to discuss adding a custom domain to your account.
    `,
    UNSUBSCRIBE_GOOGLE_POLICY_DESCRIPTION: oneLine`
        Recent changes require email senders who send more than 5,000 messages or more a day to Gmail accounts
        to include an unsubscribe link. Failing to provide an unsubscribe link can damage your sending
        reputation, and affect your ability to deliver messages to your audience.
    `,
    UNSUBSCRIBE_SETTINGS_CONDITIONS_LIST: [
        "If you are using a quorum hosted email domain (e.g. quorumoutbox.com)",
        "If your email has 5000 or more recipients",
        ...(Userdata.Permissions.qp_media_relations === true
            ? ["If your email has 50 or more press contact recipients"]
            : []),
    ],
    ADVANCED_EMAIL_TOOL_DESCRIPTION: oneLine`
        You've successfully created an email in the email designer!
    `,
    TOOLTIP_TO_OPENS: "Open metric excludes all email open events that have been prefetched by Apple.",
    TOOLTIP_TO_OPENS_WITH_PREFETCHED: oneLine`
        Open metric includes all email open events that have been prefetched by Apple.
        Prefetching allows a browser to silently fetch the necessary resources needed to display content that a user might access in the near future.
    `,
    TOOLTIP_TO_EMAIL_COUNT: oneLine`
        This number includes recipients that may be waiting until a winning variation is chosen and delivered.
    `,
    TOOLTIP_TO_NUM_RECIPIENTS: oneLine`
        The Email Recipients metric displays the total number of recipients that were selected to receive an email.
        This number may vary from the actual number of unique emails sent - where duplicates are suppressed in sending.
    `,
}

// The Tooltip component will "helpfully" preserve linebreaks in the input,
// so we're using oneLine to keep the code pretty, and strip out the newlines and extra spaces in the rendered output.
export const outboxFinalizeConstants = {
    FROM_TEXT: oneLine`
        Choose the email, and optionally the domain, that you would like this email to be sent from.
        We add a default yourorganization${outboxConstants.DEFAULT_QUORUM_DOMAIN} sending domain when your account is created;
        however, you can work with our Customer Success team to authenticate any email that you own.
    `,
    SEND_FROM_ASSIGNED_TEAM_MEMBER_TEXT: oneLine`
        This will send emails from the assignee associated with the recipient.
        If there is a primary assignment or exactly 1 assignment for a recipient,
        the from name, email and email signature will appear to come from them.
        If there is more than one assignment and no primary assignment, or no assignment at all,
        the email will appear to send from the default sender selected below.
    `,
    REPLY_TO_TEXT: oneLine`
        You can choose to have the replies from this email go to anyone in your organization.
        If Quorum is managing the email routing of your domain, the recipient will see that the reply to address is the same as the from address.
        However, if you are managing the routing of your domain yourself, you should make sure that the from and reply domains are the same
        (for best deliverability).
    `,
    REPLY_TO_ASSIGNED_TEAM_MEMBER_TEXT: oneLine`
        This will route replies to the assignee associated with the recipient.
        If there is a primary assignment or exactly 1 assignment for a recipient,
        the replies will be routed to that user. If there is more than one assignment and no primary assignment,
        or no assignment at all, the email will route replies to the default user selected below.
    `,
    CC_TEXT: oneLine`
        Choose an email, or multiple emails separated by commas, to add to the CC header of an email.
        They will receive any emails that are replied to if the recipient uses "Reply All".
    `,
    BCC_TEXT: oneLine`
        Choose an email, or multiple emails separated by commas, to receive a copy of this email upon sending.
        They will receive it from a "Quorum BCC" email account,
        and will see that the placeholders have been replaced with a random person that the message was sent to.
    `,
    PROJECT_TEXT: `Tag issues here`,
    CAMPAIGN_TEXT: `Associate this email with a Grassroots Campaign.`,

    // ABTest constants
    IS_PAST_DATE_ERROR_TEXT: "The scheduled time selected is not in the future! Please double check your sending time.",
    START_TOO_SOON_TEXT: "The scheduled time must be at least 10 minutes from now!",
    END_TIME_WITHOUT_START_TIME: "Test must have both start time and end time!",
    END_BEFORE_START_ERROR_TEXT: "Test is scheduled to end before it begins!",
    END_TOO_SOON_ERROR_TEXT: "Test is scheduled to end less than 2 hours after it started!",
    END_TOO_LATE_ERROR_TEXT: "Test is scheduled to end more than 7 days after it started!",
}

export const validationContants = {
    FROM_REQUIRED_FIELDS: "If you have a custom 'from', all fields are required",
    FROM_INVALID_EMAIL: "The From email you have specified is not valid",
    REPLY_REQUIRED_FIELDS: "If you have a custom 'reply to', all fields are required",
    REPLY_INVALID_EMAIL: "The Reply To email you have specified is not valid",
    BCC_INVALID_EMAIL: "Not all of your bcc recipients have valid email addresses",
    CC_INVALID_EMAIL: "Not all of your cc recipients have valid email addresses",
    MUST_HAVE_RECIPIENTS: "Your email must have recipients to send a message",
    MUST_HAVE_SUBJECT: "Your email must have a subject",
    MUST_HAVE_CONTENT: "Your email must have content",
    SLASH_IN_PLACEHOLDER: "Slashes are not allowed inside placeholders",
    SPACE_IN_PLACEHOLDER: "Spaces are not allowed inside placeholders",
    CONTAINS_PLACEHOLDERS_ONLY: "Your email contains only placeholders. Please add additional content to your email",
    CONTAINS_IFRAME: "IFrames are not supported with most email providers and will be flagged as spam/malware",
    CONTAINS_IMAGE_ONLY: `Please include text in your email and resend. Sending emails with only images is a common tactic used by spammers.
     Spam filters are designed to detect such practices, potentially causing your email to be flagged as junk`,
}

export default outboxConstants
